import { MAX_ARMOR, MAX_HEALTH, MAX_HUNGER } from "../config";
import { blockDef } from "../engine/blocks";
import type { Atlas } from "../engine/textures";
import type { ChestState, FurnaceState } from "../game/containers";
import { resultSlot } from "../game/crafting";
import { itemDef } from "../game/items";
import type { Inventory, SlotCell } from "../game/inventory";
import { biomeDef } from "../gen/biomes";
import type { Player } from "../game/player";
import { iconUrl } from "./icons";

export class Hud {
  readonly root: HTMLElement;
  private atlas: Atlas;
  private inv: Inventory;
  private onClose: () => void;
  private cursorEl: HTMLDivElement;
  private hotbarEl: HTMLDivElement;
  private heartsEl: HTMLDivElement;
  private hungerEl: HTMLDivElement;
  private armorEl: HTMLDivElement;
  private debugEl: HTMLDivElement;
  private flyEl: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private pauseEl: HTMLDivElement;
  private panelEl: HTMLDivElement;
  private loadEl: HTMLDivElement;
  private hintEl: HTMLDivElement;
  private openKind: "none" | "pack" | "table" | "chest" | "kiln" = "none";
  private chestState: ChestState | null = null;
  private furnaceState: FurnaceState | null = null;

  constructor(atlas: Atlas, inv: Inventory, onClose: () => void) {
    this.atlas = atlas;
    this.inv = inv;
    this.onClose = onClose;
    this.root = document.getElementById("hud")!;
    this.root.innerHTML = "";
    this.titleEl = el("div", "title-screen");
    this.loadEl = el("div", "loader");
    this.pauseEl = el("div", "pause hidden");
    this.panelEl = el("div", "panel hidden");
    this.hotbarEl = el("div", "hotbar");
    this.heartsEl = el("div", "meters hearts");
    this.hungerEl = el("div", "meters hunger");
    this.armorEl = el("div", "meters armor");
    this.debugEl = el("div", "debug");
    this.flyEl = el("div", "fly-badge hidden");
    this.hintEl = el("div", "hint");
    this.cursorEl = el("div", "drag-cursor hidden");
    const cross = el("div", "crosshair");
    this.root.append(
      this.titleEl,
      this.loadEl,
      this.pauseEl,
      this.panelEl,
      this.hotbarEl,
      this.heartsEl,
      this.hungerEl,
      this.armorEl,
      this.debugEl,
      this.flyEl,
      this.hintEl,
      cross,
      this.cursorEl,
    );
    this.buildTitle();
    this.buildPause();
    this.loadEl.textContent = "Carving the painted hills...";
    this.flyEl.textContent = "FLY";
    window.addEventListener("mousemove", this.followCursor);
  }

  private followCursor = (e: MouseEvent): void => {
    this.cursorEl.style.left = `${e.clientX + 10}px`;
    this.cursorEl.style.top = `${e.clientY + 10}px`;
  };

  private buildTitle(): void {
    this.titleEl.innerHTML = `
      <div class="title-inner">
        <p class="kicker">a painted voxel wanderer</p>
        <h1>CUBEHOLT</h1>
        <p class="lede">Third-person cube wilds. Break, place, craft, and drift over blossom vales.</p>
        <label class="seed-field">Seed
          <input id="seed-input" type="text" maxlength="16" autocomplete="off" />
        </label>
        <div class="title-actions">
          <button id="enter-world" class="btn primary">Enter world</button>
          <button id="continue-world" class="btn">Continue</button>
        </div>
        <ul class="controls">
          <li><kbd>WASD</kbd> move</li>
          <li><kbd>Space</kbd> jump / swim up</li>
          <li><kbd>Shift</kbd> sprint</li>
          <li><kbd>Ctrl</kbd> sneak</li>
          <li><kbd>F</kbd> fly</li>
          <li><kbd>V</kbd> camera</li>
          <li><kbd>E</kbd> pack</li>
          <li><kbd>LMB</kbd> break <kbd>RMB</kbd> use</li>
        </ul>
      </div>`;
  }

  private buildPause(): void {
    this.pauseEl.innerHTML = `
      <div class="pause-card">
        <h2>Paused</h2>
        <button id="resume-btn" class="btn primary">Resume</button>
        <button id="save-btn" class="btn">Save</button>
        <button id="title-btn" class="btn">Leave</button>
      </div>`;
  }

  showTitle(showContinue: boolean, seed: string): void {
    this.titleEl.classList.remove("hidden");
    const input = document.getElementById("seed-input") as HTMLInputElement;
    input.value = seed;
    document.getElementById("continue-world")!.classList.toggle("hidden", !showContinue);
    this.loadEl.classList.add("hidden");
    this.pauseEl.classList.add("hidden");
    this.panelEl.classList.add("hidden");
  }

  hideTitle(): void {
    this.titleEl.classList.add("hidden");
  }

  seedValue(): string {
    return (document.getElementById("seed-input") as HTMLInputElement).value;
  }

  onEnter(fn: () => void): void {
    document.getElementById("enter-world")!.addEventListener("click", fn);
  }
  onContinue(fn: () => void): void {
    document.getElementById("continue-world")!.addEventListener("click", fn);
  }
  onResume(fn: () => void): void {
    document.getElementById("resume-btn")!.addEventListener("click", fn);
  }
  onSave(fn: () => void): void {
    document.getElementById("save-btn")!.addEventListener("click", fn);
  }
  onLeave(fn: () => void): void {
    document.getElementById("title-btn")!.addEventListener("click", fn);
  }

  setLoading(text: string | null): void {
    if (!text) {
      this.loadEl.classList.add("hidden");
      return;
    }
    this.loadEl.classList.remove("hidden");
    this.loadEl.textContent = text;
  }

  setPaused(v: boolean): void {
    this.pauseEl.classList.toggle("hidden", !v);
  }

  playingHud(show: boolean): void {
    for (const n of [this.hotbarEl, this.heartsEl, this.hungerEl, this.armorEl, this.debugEl, this.hintEl]) {
      n.classList.toggle("hidden", !show);
    }
    document.querySelector(".crosshair")?.classList.toggle("hidden", !show);
  }

  updatePlay(player: Player, biome: number, fps: number, chunks: string): void {
    this.drawHotbar(player.inventory);
    this.drawMeters(player);
    this.flyEl.classList.toggle("hidden", !player.flying);
    const b = biomeDef(biome);
    this.debugEl.innerHTML = `
      <span>${Math.floor(player.x)} ${Math.floor(player.y)} ${Math.floor(player.z)}</span>
      <span>${b.name}</span>
      <span>${fps} fps</span>
      <span>${chunks}</span>
      <span>${player.thirdPerson ? "third" : "first"}</span>`;
    const held = player.inventory.selectedSlot();
    this.hintEl.textContent = held ? itemDef(held.id).name : "";
  }

  closePanel(): void {
    this.panelEl.classList.add("hidden");
    this.panelEl.innerHTML = "";
    this.openKind = "none";
    this.chestState = null;
    this.furnaceState = null;
  }

  openInventory(player: Player): void {
    this.openKind = "pack";
    this.renderPanel("Pack", () => {
      const body = el("div", "panel-body");
      body.append(this.doll(player), this.grid(player.inventory.craft, 2, "craft", player));
      const res = this.resultCell(false, player);
      body.append(res);
      body.append(this.grid(player.inventory.main, 9, "main", player));
      body.append(this.grid(player.inventory.hotbar, 9, "hot", player));
      return body;
    }, player);
  }

  openTable(player: Player): void {
    this.openKind = "table";
    this.renderPanel("Workbench", () => {
      const body = el("div", "panel-body");
      body.append(this.grid(player.inventory.table, 3, "table", player));
      body.append(this.resultCell(true, player));
      body.append(this.grid(player.inventory.main, 9, "main", player));
      body.append(this.grid(player.inventory.hotbar, 9, "hot", player));
      return body;
    }, player);
  }

  openChest(player: Player, chest: ChestState): void {
    this.openKind = "chest";
    this.chestState = chest;
    this.renderPanel("Chest", () => {
      const body = el("div", "panel-body");
      body.append(this.grid(chest.slots, 9, "chest", player));
      body.append(this.grid(player.inventory.main, 9, "main", player));
      body.append(this.grid(player.inventory.hotbar, 9, "hot", player));
      return body;
    }, player);
  }

  openFurnace(player: Player, furnace: FurnaceState): void {
    this.openKind = "kiln";
    this.furnaceState = furnace;
    this.renderPanel("Kiln", () => {
      const body = el("div", "panel-body furnace-body");
      const col = el("div", "furnace-col");
      col.append(this.single(furnace, "input", player));
      const flame = el("div", "flame");
      flame.textContent = furnace.burn > 0 ? "fire" : "cold";
      col.append(flame);
      col.append(this.single(furnace, "fuel", player));
      const out = this.single(furnace, "output", player);
      const cook = el("div", "cook-bar");
      cook.style.setProperty("--p", String(Math.min(1, furnace.cook / 8)));
      body.append(col, cook, out);
      body.append(this.grid(player.inventory.main, 9, "main", player));
      body.append(this.grid(player.inventory.hotbar, 9, "hot", player));
      return body;
    }, player);
  }

  tickFurnace(furnace: FurnaceState): void {
    if (this.openKind !== "kiln") return;
    const flame = this.panelEl.querySelector(".flame");
    if (flame) flame.textContent = furnace.burn > 0 ? "fire" : "cold";
    const cook = this.panelEl.querySelector(".cook-bar") as HTMLElement | null;
    if (cook) cook.style.setProperty("--p", String(Math.min(1, furnace.cook / 8)));
  }

  refreshIfOpen(player: Player, extra?: { chest?: ChestState; furnace?: FurnaceState }): void {
    if (this.panelEl.classList.contains("hidden")) return;
    if (this.openKind === "pack") this.openInventory(player);
    else if (this.openKind === "table") this.openTable(player);
    else if (this.openKind === "chest" && (extra?.chest || this.chestState)) {
      this.openChest(player, extra?.chest ?? this.chestState!);
    } else if (this.openKind === "kiln" && (extra?.furnace || this.furnaceState)) {
      this.openFurnace(player, extra?.furnace ?? this.furnaceState!);
    }
  }

  private renderPanel(title: string, body: () => HTMLElement, player: Player): void {
    this.syncCraft(player);
    this.panelEl.classList.remove("hidden");
    this.panelEl.innerHTML = "";
    const card = el("div", "panel-card");
    const head = el("div", "panel-head");
    head.innerHTML = `<h2>${title}</h2>`;
    const close = el("button", "icon-btn");
    close.textContent = "close";
    close.addEventListener("click", () => this.onClose());
    head.append(close);
    card.append(head, body());
    this.panelEl.append(card);
    this.drawCursor();
  }

  private syncCraft(player: Player): void {
    player.inventory.result = resultSlot(player.inventory.craft, 2);
    player.inventory.tableResult = resultSlot(player.inventory.table, 3);
  }

  private doll(player: Player): HTMLElement {
    const wrap = el("div", "doll");
    const labels = ["head", "chest", "legs", "feet"];
    for (let i = 0; i < 4; i++) {
      wrap.append(this.slotEl(player.inventory.armor, i, "armor", player, labels[i]));
    }
    return wrap;
  }

  private grid(slots: SlotCell[], cols: number, kind: string, player: Player): HTMLElement {
    const g = el("div", `slot-grid cols-${cols}`);
    for (let i = 0; i < slots.length; i++) g.append(this.slotEl(slots, i, kind, player));
    return g;
  }

  private resultCell(table: boolean, player: Player): HTMLElement {
    const wrap = el("div", "result");
    const slot = el("button", "slot result-slot");
    const s = table ? player.inventory.tableResult : player.inventory.result;
    this.paintSlot(slot, s);
    slot.addEventListener("click", () => {
      player.inventory.takeResult(table);
      this.syncCraft(player);
      if (table) this.openTable(player);
      else this.openInventory(player);
    });
    wrap.append(slot);
    return wrap;
  }

  private single(f: FurnaceState, key: "input" | "fuel" | "output", player: Player): HTMLElement {
    const slot = el("button", "slot");
    this.paintSlot(slot, f[key]);
    slot.addEventListener("click", (e) => {
      const right = (e as MouseEvent).button === 2;
      const bag: SlotCell[] = [f[key]];
      player.inventory.clickSlot(bag, 0, right, e.shiftKey, player.inventory.hotbar);
      f[key] = bag[0] ?? null;
      this.openFurnace(player, f);
    });
    slot.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      const bag: SlotCell[] = [f[key]];
      player.inventory.clickSlot(bag, 0, true, false);
      f[key] = bag[0] ?? null;
      this.openFurnace(player, f);
    });
    return slot;
  }

  private slotEl(
    bag: SlotCell[],
    index: number,
    kind: string,
    player: Player,
    label?: string,
  ): HTMLElement {
    const slot = el("button", `slot${player.inventory.selected === index && kind === "hot" ? " selected" : ""}`);
    if (label) slot.dataset.label = label;
    this.paintSlot(slot, bag[index] ?? null);
    const dest = kind === "hot" ? player.inventory.main : player.inventory.hotbar;
    slot.addEventListener("click", (e) => {
      if (kind === "armor") {
        player.inventory.clickSlot(bag, index, false, e.shiftKey, dest);
      } else {
        player.inventory.clickSlot(bag, index, false, e.shiftKey, dest);
      }
      this.syncCraft(player);
      this.redrawOpen(player);
    });
    slot.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      player.inventory.clickSlot(bag, index, true, false);
      this.syncCraft(player);
      this.redrawOpen(player);
    });
    return slot;
  }

  private redrawOpen(player: Player): void {
    this.refreshIfOpen(player);
    this.drawHotbar(player.inventory);
    this.drawCursor();
  }

  private paintSlot(el: HTMLElement, s: SlotCell): void {
    el.innerHTML = "";
    if (!s) return;
    const img = document.createElement("img");
    img.src = iconUrl(s.id, this.atlas);
    img.alt = itemDef(s.id).name;
    img.draggable = false;
    el.append(img);
    if (s.count > 1) {
      const n = document.createElement("span");
      n.className = "count";
      n.textContent = String(s.count);
      el.append(n);
    }
    if (s.dur !== undefined && itemDef(s.id).durability) {
      const max = itemDef(s.id).durability!;
      if (s.dur < max) {
        const bar = document.createElement("i");
        bar.className = "dur";
        bar.style.width = `${(s.dur / max) * 100}%`;
        el.append(bar);
      }
    }
  }

  private drawHotbar(inv: Inventory): void {
    this.hotbarEl.innerHTML = "";
    for (let i = 0; i < 9; i++) {
      const s = el("div", `slot${i === inv.selected ? " selected" : ""}`);
      this.paintSlot(s, inv.hotbar[i] ?? null);
      this.hotbarEl.append(s);
    }
  }

  private drawMeters(player: Player): void {
    this.heartsEl.innerHTML = pips(player.health, MAX_HEALTH, "hp");
    this.hungerEl.innerHTML = pips(player.hunger, MAX_HUNGER, "food");
    this.armorEl.innerHTML = pips(player.inventory.armorPoints(), MAX_ARMOR, "arm");
  }

  private drawCursor(): void {
    const s = this.inv.cursor;
    if (!s) {
      this.cursorEl.classList.add("hidden");
      this.cursorEl.innerHTML = "";
      return;
    }
    this.cursorEl.classList.remove("hidden");
    this.paintSlot(this.cursorEl, s);
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  n.className = cls;
  return n;
}

function pips(value: number, max: number, kind: string): string {
  const full = Math.ceil(max / 2);
  let html = "";
  for (let i = 0; i < full; i++) {
    const v = value - i * 2;
    const state = v >= 2 ? "full" : v >= 1 ? "half" : "empty";
    html += `<i class="pip ${kind} ${state}"></i>`;
  }
  return html;
}

export function blockColor(id: number): number {
  const name = blockDef(id).name.toLowerCase();
  if (name.includes("grass")) return 0x5aa83c;
  if (name.includes("dirt")) return 0x7a5434;
  if (name.includes("stone") || name.includes("cobble")) return 0x77777c;
  if (name.includes("sand")) return 0xd6c480;
  if (name.includes("leaf") || name.includes("blossom") || name.includes("needle")) return 0x3e8434;
  if (name.includes("log") || name.includes("plank")) return 0x8a6230;
  return 0x9a9a9a;
}
